import { AnimatePresence, motion } from "framer-motion"
import { ArrowDown, ThumbsDown, ThumbsUp } from "lucide-react"
import { ComponentProps, forwardRef, useCallback, useRef, useState, type ReactElement } from "react"
import { Button } from "../../atoms/Button"
import { useAutoScroll } from "../../hooks/useAutoScroll"
import { cn } from "../../utils"
import { ChatMessage, Message } from "./ChatMessage"
import { CopyButton } from "./CopyButton"
import {
  MessageInput,
  type MessageInputMenuControl,
  type NoteInputComponentProps,
} from "./MessageInput"
import { MessageList, NoteComponentProps, NoteItem } from "./MessageList"
import { PromptSuggestions } from "./PromptSuggestions"

interface ChatPropsBase {
  handleSubmit: (
    event?: { preventDefault?: () => void },
    options?: { experimental_attachments?: FileList },
  ) => Promise<void> | void
  onUploadFiles?: (files: File[]) => Promise<void> | void
  messages: Array<Message>
  input: string
  disabled?: boolean
  hidden?: boolean
  className?: string
  handleInputChange: React.ChangeEventHandler<HTMLTextAreaElement>
  isGenerating: boolean
  typingLabel?: string
  allowAttachments?: boolean
  acceptedFileTypes?: readonly string[]
  uploadFilesLabel?: string
  stop?: () => void
  interruptMessage: string
  onRateResponse?: (messageId: string, rating: "thumbs-up" | "thumbs-down") => void
  setMessages?: (messages: Message[]) => void
  transcribeAudio?: (blob: Blob) => Promise<string>
  animation?: ComponentProps<typeof MessageList>["animation"]
  locale?: string
}

interface ChatPropsWithEmptyState extends ChatPropsBase {
  emptyState: React.ReactNode
}

interface ChatPropsWithSuggestions extends ChatPropsBase {
  append: (message: { role: "user"; content: string }) => void
  suggestions: string[]
  label: string
}

export type ColorConfig = Record<"user" | "assistant", React.CSSProperties>

type ChatProps = (ChatPropsWithEmptyState | ChatPropsWithSuggestions) & {
  copyMessage?: string
  errorMessage?: string
  inputMaxLength?: number
  placeholder?: string
  notePlaceholder?: string
  supportCopy: {
    questionCreated: string
    questionSolvedAutomatically: string
    ticketCreated: string
    questionSubjectLabel?: string
    questionSummaryLabel?: string
  }
  opportunityCopy?: string
  renderCapabilityExecutions?: (executions: Message["capabilityExecutions"]) => React.ReactNode
  renderCustomerLogMarkers?: ComponentProps<typeof ChatMessage>["renderCustomerLogMarkers"]
  colorConfig?: ColorConfig
  publicMode?: boolean
  isFakeCustomer?: boolean
  isClosed?: boolean
  isNoteMode?: boolean
  customControlsLeft?: React.ReactNode
  customControlsRight?: React.ReactNode
  customControlsMenu?: MessageInputMenuControl[]
  replyMessageCopy?: (kind: Message["kind"]) => string
  onSelectMessage?: (message: Message) => void
  messageNotGeneratedByPermutCopy: string
  messagePendingCopy?: string
  messagePendingTooltipCopy?: string
  messageIsWhatsappTemplateCopy: string
  messageIsAmphoraCopy: string
  messageExternalSenderCopy: string
  messageExternalDeletedCopy: string
  suppressLinks?: boolean
  hiddenLinkCopy?: string
  noteItems?: NoteItem[]
  onEditNote?: (id: string, text: string) => Promise<void>
  onDeleteNote?: (id: string) => void
  noteComponent?: React.ComponentType<NoteComponentProps>
  noteInputComponent?: React.ComponentType<NoteInputComponentProps>
  scrollHeader?: React.ReactNode
}

export function Chat({
  messages,
  handleSubmit,
  input,
  handleInputChange,
  stop,
  isGenerating,
  typingLabel,
  className,
  onRateResponse,
  setMessages,
  transcribeAudio,
  copyMessage,
  errorMessage,
  inputMaxLength,
  placeholder,
  notePlaceholder,
  disabled,
  hidden,
  allowAttachments = false,
  acceptedFileTypes,
  uploadFilesLabel,
  interruptMessage,
  animation,
  supportCopy,
  opportunityCopy,
  renderCapabilityExecutions,
  renderCustomerLogMarkers,
  colorConfig,
  publicMode = false,
  isFakeCustomer = false,
  isClosed,
  isNoteMode,
  customControlsLeft,
  customControlsRight,
  customControlsMenu,
  replyMessageCopy,
  onSelectMessage,
  messageNotGeneratedByPermutCopy,
  messagePendingCopy,
  messagePendingTooltipCopy,
  messageIsWhatsappTemplateCopy,
  messageIsAmphoraCopy,
  messageExternalSenderCopy,
  messageExternalDeletedCopy,
  suppressLinks,
  hiddenLinkCopy,
  locale,
  noteItems,
  onEditNote,
  onDeleteNote,
  noteComponent,
  noteInputComponent,
  onUploadFiles,
  scrollHeader,
  ...props
}: ChatProps) {
  const isEmpty = messages.length === 0

  const messagesRef = useRef(messages)
  messagesRef.current = messages

  // Enhanced stop function that marks pending tool calls as cancelled
  const handleStop = useCallback(() => {
    stop?.()

    if (!setMessages) return

    const latestMessages = [...messagesRef.current]
    const lastAssistantMessage = latestMessages.findLast((m) => m.role === "assistant")

    if (!lastAssistantMessage) return

    let needsUpdate = false
    let updatedMessage = { ...lastAssistantMessage }

    if (lastAssistantMessage.toolInvocations) {
      const updatedToolInvocations = lastAssistantMessage.toolInvocations.map((toolInvocation) => {
        if (toolInvocation.state === "call") {
          needsUpdate = true
          return {
            ...toolInvocation,
            state: "result",
            result: {
              content: "Tool execution was cancelled",
              __cancelled: true, // Special marker to indicate cancellation
            },
          } as const
        }
        return toolInvocation
      })

      if (needsUpdate) {
        updatedMessage = {
          ...updatedMessage,
          toolInvocations: updatedToolInvocations,
        }
      }
    }

    if (lastAssistantMessage.parts && lastAssistantMessage.parts.length > 0) {
      const updatedParts = lastAssistantMessage.parts.map((part) => {
        if (
          part.type === "tool-invocation" &&
          part.toolInvocation &&
          part.toolInvocation.state === "call"
        ) {
          needsUpdate = true
          return {
            ...part,
            toolInvocation: {
              ...part.toolInvocation,
              state: "result",
              result: {
                content: "Tool execution was cancelled",
                __cancelled: true,
              },
            },
          }
        }
        return part
      })

      if (needsUpdate) {
        updatedMessage = {
          ...updatedMessage,
          parts: updatedParts as Message["parts"],
        }
      }
    }

    if (needsUpdate) {
      const messageIndex = latestMessages.findIndex((m) => m.id === lastAssistantMessage.id)
      if (messageIndex !== -1) {
        latestMessages[messageIndex] = updatedMessage
        setMessages(latestMessages)
      }
    }
  }, [stop, setMessages, messagesRef])

  const messageOptions = useCallback(
    (message: Message) => ({
      actions: onRateResponse ? (
        <div className="flex items-center space-x-1">
          {message.content && (
            <div className="border-r pr-1">
              <CopyButton
                content={message.content}
                copyMessage={copyMessage}
                errorMessage={errorMessage}
                positioning={false}
              />
            </div>
          )}
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            onClick={() => onRateResponse(message.id, "thumbs-up")}
          >
            <ThumbsUp className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            onClick={() => onRateResponse(message.id, "thumbs-down")}
          >
            <ThumbsDown className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        message.content && (
          <CopyButton
            content={message.content}
            copyMessage={copyMessage}
            errorMessage={errorMessage}
            positioning={false}
          />
        )
      ),
      ...(onSelectMessage ? { onClick: () => onSelectMessage(message) } : {}),
    }),
    [onRateResponse, onSelectMessage],
  )

  return (
    <ChatContainer className={className}>
      {isEmpty && "append" in props && "suggestions" in props && "label" in props ? (
        <PromptSuggestions
          label={props.label}
          append={props.append}
          suggestions={props.suggestions}
        />
      ) : null}
      {isEmpty && !isGenerating && "emptyState" in props ? props.emptyState : null}

      {messages.length > 0 || isGenerating || (noteItems?.length ?? 0) > 0 ? (
        <ChatMessages messages={messages} isGenerating={isGenerating}>
          {scrollHeader}
          <MessageList
            messages={messages}
            noteItems={noteItems}
            onEditNote={onEditNote}
            onDeleteNote={onDeleteNote}
            noteComponent={noteComponent}
            isFakeCustomer={isFakeCustomer}
            isTyping={isGenerating}
            typingLabel={typingLabel}
            messageOptions={messageOptions}
            className="pt-2 pb-4"
            animation={animation}
            supportCopy={supportCopy}
            opportunityCopy={opportunityCopy}
            renderCapabilityExecutions={renderCapabilityExecutions}
            renderCustomerLogMarkers={renderCustomerLogMarkers}
            colorConfig={colorConfig}
            publicMode={publicMode}
            replyMessageCopy={replyMessageCopy}
            messageNotGeneratedByPermutCopy={messageNotGeneratedByPermutCopy}
            messagePendingCopy={messagePendingCopy}
            messagePendingTooltipCopy={messagePendingTooltipCopy}
            messageIsWhatsappTemplateCopy={messageIsWhatsappTemplateCopy}
            messageIsAmphoraCopy={messageIsAmphoraCopy}
            messageExternalSenderCopy={messageExternalSenderCopy}
            messageExternalDeletedCopy={messageExternalDeletedCopy}
            suppressLinks={suppressLinks}
            hiddenLinkCopy={hiddenLinkCopy}
            locale={locale}
          />
        </ChatMessages>
      ) : null}

      {!hidden && (
        <>
          <ChatForm
            className="mt-auto sticky bottom-2.5 -mx-1 px-1 z-10 relative"
            isPending={isGenerating}
            handleSubmit={handleSubmit}
          >
            {({ files, setFiles }) => (
              <MessageInput
                maxLength={isNoteMode ? undefined : inputMaxLength}
                value={input}
                onChange={handleInputChange}
                onUploadFiles={onUploadFiles}
                allowAttachments={allowAttachments}
                acceptedFileTypes={acceptedFileTypes}
                uploadFilesLabel={uploadFilesLabel}
                files={files}
                setFiles={setFiles}
                stop={stop ? handleStop : undefined}
                isGenerating={isGenerating}
                disabled={disabled}
                transcribeAudio={transcribeAudio}
                placeholder={isNoteMode && notePlaceholder ? notePlaceholder : placeholder}
                isFakeCustomer={isFakeCustomer}
                interruptMessage={interruptMessage}
                isClosed={isClosed}
                isNoteMode={isNoteMode}
                customControlsLeft={customControlsLeft}
                customControlsRight={customControlsRight}
                customControlsMenu={customControlsMenu}
                NoteInputComponent={noteInputComponent}
              />
            )}
          </ChatForm>
        </>
      )}
    </ChatContainer>
  )
}
Chat.displayName = "Chat"

export function ChatMessages({
  messages,
  isGenerating,
  children,
}: React.PropsWithChildren<{
  messages: Message[]
  isGenerating?: boolean
}>) {
  const { containerRef, scrollToBottom, handleScroll, shouldAutoScroll, handleTouchStart } =
    useAutoScroll([messages, isGenerating])

  return (
    <div
      className="grid grid-cols-1 overflow-y-auto min-h-[320px] relative px-[1px] pb-2 scroll-smooth"
      ref={containerRef}
      onScroll={handleScroll}
      onTouchStart={handleTouchStart}
    >
      <div className="max-w-full [grid-column:1/1] [grid-row:1/1]">{children}</div>
      <div className="sticky bottom-0 right-0 h-0 w-full">
        <AnimatePresence>
          {!shouldAutoScroll && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="absolute bottom-4 right-4 z-50 pointer-events-none"
            >
              <Button
                onClick={scrollToBottom}
                className={cn("pointer-events-auto h-8 w-8 rounded-full bg-secondary shadow-md")}
                size="icon"
                variant="ghost"
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export const ChatContainer = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("grid w-full max-h-full grid-rows-[1fr_auto] gap-0", className)}
        {...props}
      />
    )
  },
)
ChatContainer.displayName = "ChatContainer"

interface ChatFormProps {
  className?: string
  isPending: boolean
  handleSubmit: (
    event?: { preventDefault?: () => void },
    options?: { experimental_attachments?: FileList },
  ) => Promise<void> | void
  children: (props: {
    files: File[] | null
    setFiles: React.Dispatch<React.SetStateAction<File[] | null>>
  }) => ReactElement
}

export const ChatForm = forwardRef<HTMLFormElement, ChatFormProps>(
  ({ children, handleSubmit, className }, ref) => {
    const [files, setFiles] = useState<File[] | null>(null)

    const onSubmit = async (event: React.FormEvent) => {
      if (!files) {
        await handleSubmit(event)
        return
      }

      const fileList = createFileList(files)
      await handleSubmit(event, { experimental_attachments: fileList })
      setFiles(null)
    }

    return (
      <form ref={ref} onSubmit={onSubmit} className={className}>
        {children({ files, setFiles })}
      </form>
    )
  },
)
ChatForm.displayName = "ChatForm"

function createFileList(files: File[] | FileList): FileList {
  const dataTransfer = new DataTransfer()
  for (const file of Array.from(files)) {
    dataTransfer.items.add(file)
  }
  return dataTransfer.files
}
